import {
  Case,
  Claim,
  getAwardedValue,
  Issue,
  IssueRelation,
  NamedEntity,
} from "./CaseModel.ts";
import { getSettledDefendantIds } from "./CaseModelUtility.ts";
import { damageName } from "./damages.ts";
import { judgmentOf } from "./judgment.ts";
import { copyIdAgainst } from "./reserve.ts";
import { enteredChance } from "./winChance.ts";

/** A claim or an issue as a judgment decides it against one defendant. */
export type DecisionNode = {
  // the id the calculation decides it under
  readonly id: string;
  // the id it has in the case
  readonly issueId: string;
  readonly title: string;
  readonly relation?: IssueRelation;
  readonly children: DecisionNode[];
};

/** A claim of a judgment: what is to be decided against each defendant. */
export type ClaimDecisions = {
  readonly claim: Claim;
  readonly perDefendant: {
    readonly defendant: NamedEntity;
    readonly root: DecisionNode;
  }[];
};

const isLeaf = (issue: Issue) => !issue.subIssues?.length;
const isShared = (issue: Issue) => issue.generic === true && isLeaf(issue);

function nodeOf(
  issue: Issue,
  defendant: NamedEntity,
  several: boolean,
): DecisionNode | undefined {
  // an issue that does not concern the defendant is not decided against it
  if (
    isLeaf(issue) &&
    !issue.generic &&
    enteredChance(issue.perDefendantWinChance, defendant.id) === undefined
  )
    return undefined;
  return {
    id:
      !several || isShared(issue)
        ? issue.id
        : copyIdAgainst(issue.id, defendant.id),
    issueId: issue.id,
    title: issue.title,
    relation: issue.relation,
    children: (issue.subIssues ?? []).flatMap(
      (sub) => nodeOf(sub, defendant, several) ?? [],
    ),
  };
}

function rootOf(
  claim: Claim,
  defendant: NamedEntity,
  several: boolean,
): DecisionNode | undefined {
  const id = several ? copyIdAgainst(claim.id, defendant.id) : claim.id;
  const base = {
    id,
    issueId: claim.id,
    title: claim.title,
    relation: claim.relation,
  };
  if (claim.issues?.length)
    return {
      ...base,
      children: claim.issues.flatMap(
        (issue) => nodeOf(issue, defendant, several) ?? [],
      ),
    };
  // a claim that is one decision itself: a generic one for all its defendants
  if (several && claim.generic)
    return {
      ...base,
      children: [{ ...base, id: claim.id, relation: undefined, children: [] }],
    };
  if (
    several &&
    enteredChance(claim.perDefendantWinChance, defendant.id) === undefined
  )
    return undefined;
  return { ...base, children: [] };
}

/**
 * What a judgment of the case decides: every claim and issue against each
 * defendant still in the case, under the ids of the calculation (see
 * CasePreprocessor).
 */
export function decisionsOf({ claims, settlements }: Case): ClaimDecisions[] {
  return (claims ?? []).flatMap((claim) => {
    const defendants = claim.defendants ?? [];
    const settled = getSettledDefendantIds(claim, settlements);
    const perDefendant = defendants.flatMap((defendant) => {
      if (settled.has(defendant.id)) return [];
      const root = rootOf(claim, defendant, defendants.length > 1);
      return root ? [{ defendant, root }] : [];
    });
    return perDefendant.length > 0 ? [{ claim, perDefendant }] : [];
  });
}

/** The lowest issues of a node: the decisions it comes down to. */
export const leavesOf = (node: DecisionNode): DecisionNode[] =>
  node.children.length === 0 ? [node] : node.children.flatMap(leavesOf);

/** Every lowest issue of the decisions, each once. */
export function decisionIds(decisions: ClaimDecisions[]): string[] {
  return [
    ...new Set(
      decisions.flatMap(({ perDefendant }) =>
        perDefendant.flatMap(({ root }) => leavesOf(root).map(({ id }) => id)),
      ),
    ),
  ];
}

/**
 * Whether a claim or issue is won with the lowest issues won, by its relation:
 * all of them unless it says otherwise, as the calculation has it.
 */
export function isWon(node: DecisionNode, won: ReadonlySet<string>): boolean {
  if (node.children.length === 0) return won.has(node.id);
  const results = node.children.map((child) => isWon(child, won));
  switch (node.relation) {
    case IssueRelation.Or:
      return results.some(Boolean);
    case IssueRelation.Xor:
      return results.filter(Boolean).length === 1;
    default:
      return results.every(Boolean);
  }
}

/** What a judgment can award of a damage on a claim or an issue. */
export type AwardRow = {
  readonly claimId: string;
  readonly issueId: string;
  readonly damageId: string;
  readonly name: string;
  readonly claimed: number;
  // what the case expected to be awarded
  readonly expected: number;
};

/**
 * The damages won with the lowest issues won: of each claim or issue won
 * against at least one defendant, what it claims.
 */
export function awardRows(c: Case, won: ReadonlySet<string>): AwardRow[] {
  const rows: AwardRow[] = [];
  for (const { claim, perDefendant } of decisionsOf(c)) {
    const wonIds = new Set<string>();
    const collect = (node: DecisionNode) => {
      if (isWon(node, won)) wonIds.add(node.issueId);
      node.children.forEach(collect);
    };
    perDefendant.forEach(({ root }) => collect(root));
    const visit = (issue: Issue) => {
      if (wonIds.has(issue.id))
        for (const quantum of issue.quantums ?? [])
          rows.push({
            claimId: claim.id,
            issueId: issue.id,
            damageId: quantum.damageId,
            name: damageName(c.damages, quantum.damageId),
            claimed: quantum.value,
            expected: getAwardedValue(quantum),
          });
      issue.subIssues?.forEach(visit);
    };
    visit(claim);
    claim.issues?.forEach(visit);
  }
  return rows;
}

/**
 * What the judgment in force decided of each claim and issue, by its id in the
 * case: per defendant, whether it was upheld. Undefined without a judgment.
 */
export function judgedRowsOf(
  c: Case,
): Map<string, { [defendantId: string]: boolean }> | undefined {
  const judgment = judgmentOf(c);
  if (judgment === undefined) return undefined;
  const won = new Set(judgment.scenario.won);
  const rows = new Map<string, { [defendantId: string]: boolean }>();
  for (const { perDefendant } of decisionsOf(c))
    for (const { defendant, root } of perDefendant) {
      const collect = (node: DecisionNode) => {
        rows.set(node.issueId, {
          ...rows.get(node.issueId),
          [defendant.id]: isWon(node, won),
        });
        node.children.forEach(collect);
      };
      collect(root);
    }
  return rows;
}
